import { Helmet } from "react-helmet-async";
import { memo, Suspense, lazy, useMemo, useEffect } from "react";
import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { SchemaMarkup } from "@/components/seo/schema/SchemaMarkup";
import { generateHomepageGraph } from "@/utils/schemaMarkup";
import { useCanonicalUrl } from "@/hooks/useCanonicalUrl";
import { FavoritesBar } from "@/components/listings/card/FavoriteButton";
import { SafeLink } from "@/components/layout/SafeLink";
import { useCountry } from "@/hooks/useCountry";
import LazyOnVisible from "@/components/performance/LazyOnVisible";
import CardSkeletonGrid from "@/components/home/CardSkeletonGrid";

// GSAP is dynamically imported inside the scroll-reveal effect below to keep
// it out of the critical home bundle (~50KB of parse+eval saved for LCP/INP on mobile).

import HeroV3 from "@/components/home/HeroV3";
import ValuationCTABanner from "@/components/home/ValuationCTABanner";
import CategoryTilesV2 from "@/components/home/CategoryTilesV2";
import TrustedDealers from "@/components/home/TrustedDealers";
import HomeSEOContent from "@/components/home/HomeSEOContent";

import RecommendedListingsV2 from "@/components/home/RecommendedListingsV2";
const PriceAlertBanner = lazy(() => import("@/components/home/PriceAlertBanner"));
const BrandTilesV2 = lazy(() => import("@/components/home/BrandTilesV2"));
const DealerCTACardV2 = lazy(() => import("@/components/home/DealerCTACardV2"));


const FinancingCTAHome = lazy(() => import("@/components/home/FinancingCTAHome"));
const LiveActivityStrip = lazy(() => import("@/components/home/LiveActivityStrip"));
const RecentlySoldStrip = lazy(() => import("@/components/home/RecentlySoldStrip"));
// DealerImportBanner removed: replaced by DealerB2BBand directly under hero (same message, better placement)
const RecommendedOffers = lazy(() => import("@/components/home/RecommendedOffers"));
const AutoPromotedListings = lazy(() => import("@/components/listings/card/AutoPromotedListings"));
const LatestNewsSection = lazy(() => import("@/components/home/LatestNewsSection"));
const SellCTABanner = lazy(() => import("@/components/home/SellCTABanner"));
const AboutIntroBlock = lazy(() => import("@/components/home/AboutIntroBlock"));
const PressMention = lazy(() => import("@/components/home/PressMention"));
const WhyUsStack = lazy(() => import("@/components/home/WhyUsStack"));

const PopularBrands = lazy(() => import("@/components/home/PopularBrands"));
const PopularCities = lazy(() => import("@/components/home/PopularCities"));
const SEOInternalLinksBlock = lazy(() => import("@/components/home/SEOInternalLinksBlock"));

const AnswerCapsules = lazy(() => import("@/components/seo/AnswerCapsules"));
const FAQSection = lazy(() => import("@/components/seo/FAQSection").then(m => ({ default: m.FAQSection })));

const SITE_URL = 'https://roautomarket.ro';
const OG_IMAGE = `${SITE_URL}/og-roautomarket.png`;

const SEO_BY_COUNTRY: Record<string, { lang: string; title: string; description: string; ogTitle: string; ogDesc: string; twitterTitle: string; twitterDesc: string; links: { to: string; label: string }[] }> = {
  RO: {
    lang: 'ro',
    title: 'Marketplace auto România | Dealeri verificați | RO Auto Market',
    description: 'Mașini second hand de la dealeri verificați ANAF și RISCO și vânzători privați. Suni direct vânzătorul și nu plătești comision.',
    ogTitle: 'Marketplace auto România | Dealeri verificați | RO Auto Market',
    ogDesc: 'Mașini second hand de la dealeri verificați ANAF și RISCO și vânzători privați. Suni direct vânzătorul și nu plătești comision.',
    twitterTitle: 'Marketplace auto România | Dealeri verificați | RO Auto Market',
    twitterDesc: 'Mașini second hand de la dealeri verificați ANAF și RISCO și vânzători privați. Suni direct vânzătorul și nu plătești comision.',
    links: [
      { to: '/vehicles', label: 'Toate Vehiculele' },
      { to: '/pricing', label: 'Prețuri dealeri' },
      { to: '/dealeri-verificati', label: 'Dealeri verificați' },
      { to: '/masini-orase', label: 'Mașini pe orașe' },
      { to: '/blog', label: 'Blog Auto' },
      { to: '/contact', label: 'Contact' },
    ],
  },
  UK: {
    lang: 'en',
    title: 'Used Cars & Vehicles: Verified Dealers | RO Auto Market UK',
    description: 'RO Auto Market UK: multi-category platform with verified dealers. Cars, trucks, machinery, boats. MOT history, ULEZ compliance, no sales commission.',
    ogTitle: 'RO Auto Market UK: Cars, Trucks & Vehicles from Verified Dealers',
    ogDesc: 'Search, compare and buy used vehicles from verified dealers. No sales commission.',
    twitterTitle: 'Used Cars UK: Buy listings from verified dealers | RO Auto Market',
    twitterDesc: 'The UK platform for buying used vehicles from verified dealers. Full MOT history, ULEZ compliance and transparent pricing.',
    links: [
      { to: '/uk/vehicles', label: 'All Vehicles' },
      { to: '/uk/pricing', label: 'Dealer Pricing' },
      { to: '/uk/verified-dealers', label: 'Verified Dealers' },
      { to: '/uk/blog', label: 'Auto Blog' },
      { to: '/uk/contact', label: 'Contact' },
    ],
  },
  DE: {
    lang: 'de',
    title: 'Gebrauchtwagen & Fahrzeuge: Geprüfte Händler | RO Auto Market',
    description: 'Sehen Sie verfügbare Fahrzeuganzeigen von Händlern und Privatpersonen. Vergleichen Sie Angebote und kontaktieren Sie Verkäufer direkt.',
    ogTitle: 'RO Auto Market: Autos, LKW & Fahrzeuge von geprüften Händlern',
    ogDesc: 'Suchen, vergleichen und kaufen Sie Gebrauchtwagen von geprüften Händlern. Keine Verkaufsprovision.',
    twitterTitle: 'Gebrauchtwagen Deutschland: Geprüfte Fahrzeuge | RO Auto Market',
    twitterDesc: 'Sehen Sie verfügbare Gebrauchtfahrzeuge und kontaktieren Sie Verkäufer direkt.',
    links: [
      { to: '/de/vehicles', label: 'Alle Fahrzeuge' },
      { to: '/de/pricing', label: 'Händlerpreise' },
      { to: '/de/verified-dealers', label: 'Geprüfte Händler' },
      { to: '/de/blog', label: 'Auto Blog' },
      { to: '/de/contact', label: 'Kontakt' },
    ],
  },
};

const SectionFallback = ({ h = "200px" }: { h?: string }) => (
  <div style={{ height: h }} className="animate-pulse bg-muted rounded-2xl mx-4" />
);

const Index = () => {
  const { country } = useCountry();
  const canonicalUrl = useCanonicalUrl();
  const seo = useMemo(() => SEO_BY_COUNTRY[country] || SEO_BY_COUNTRY.RO, [country]);

  // Scroll-triggered reveal for below-the-fold sections.
  // GSAP is dynamically imported and scheduled to idle time so it never blocks
  // the initial paint / LCP on mobile.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    const run = async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);

      const ctx = gsap.context(() => {
        const els = document.querySelectorAll<HTMLElement>("[data-gsap-reveal]");
        els.forEach((el) => {
          gsap.fromTo(
            el,
            { opacity: 0, y: 40 },
            {
              opacity: 1,
              y: 0,
              duration: 0.6,
              ease: "power2.out",
              scrollTrigger: { trigger: el, start: "top 85%", once: true },
            }
          );
        });
      });
      cleanup = () => ctx.revert();
    };

    const idle = (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(run, { timeout: 2500 })
      : window.setTimeout(run, 800);

    return () => {
      cancelled = true;
      if ((window as any).cancelIdleCallback && typeof idle === "number") {
        (window as any).cancelIdleCallback(idle);
      } else {
        clearTimeout(idle);
      }
      cleanup?.();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background pb-[70px] md:pb-0">
      <Helmet>
        <html lang={seo.lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.ogTitle} />
        <meta property="og:description" content={seo.ogDesc} />
        <meta name="robots" content="index, follow" />
        {/* Canonical is emitted once by the route head() (src/lib/seo/route-head): do not duplicate here */}
        {/* Preconnect to Supabase already declared statically in index.html: no duplicate here */}
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@roautomarket" />
        <meta name="twitter:title" content={seo.twitterTitle} />
        <meta name="twitter:description" content={seo.twitterDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <SEOHreflang canonicalUrl={canonicalUrl} />
      <SchemaMarkup schema={generateHomepageGraph(country)} />
      <FavoritesBar />

      <main>
        <HeroV3 />

        <ValuationCTABanner />

        <CategoryTilesV2 />



        <div data-gsap-reveal>
          <RecommendedListingsV2 />
        </div>

        {/* Social proof: vândute recent: afișat sus pentru vizibilitate */}
        <Suspense fallback={<SectionFallback h="280px" />}>
          <RecentlySoldStrip />
        </Suspense>

        <LazyOnVisible minHeight="120px" data-gsap-reveal>
          <Suspense fallback={<SectionFallback h="120px" />}>
            <PriceAlertBanner />
          </Suspense>
        </LazyOnVisible>

        <LazyOnVisible minHeight="220px" data-gsap-reveal>
          <Suspense fallback={<SectionFallback h="220px" />}>
            <BrandTilesV2 />
          </Suspense>
        </LazyOnVisible>

        <LazyOnVisible minHeight="180px" data-gsap-reveal>
          <Suspense fallback={<SectionFallback h="180px" />}>
            <DealerCTACardV2 />
          </Suspense>
        </LazyOnVisible>

        {/* Secțiuni existente păstrate */}
        <TrustedDealers />

        <LazyOnVisible minHeight="420px">
          <Suspense fallback={<SectionFallback h="420px" />}>
            <WhyUsStack />
          </Suspense>
        </LazyOnVisible>



        <Suspense fallback={<SectionFallback h="120px" />}>
          <LiveActivityStrip />
        </Suspense>



        {/* SocialProofSection eliminat: statistici deja afișate în hero */}


        {/* Persoane fizice: mutat sus pentru vizibilitate, după primele liste de dealeri */}
        <LazyOnVisible minHeight="320px" data-gsap-reveal>
          <Suspense fallback={<SectionFallback h="320px" />}>
            <SellCTABanner />
          </Suspense>
        </LazyOnVisible>

        <LazyOnVisible minHeight="400px" data-gsap-reveal>
          <Suspense fallback={<CardSkeletonGrid count={3} />}>
            <PopularCities />
          </Suspense>
        </LazyOnVisible>

        <LazyOnVisible minHeight="120px" data-gsap-reveal>
          <Suspense fallback={null}>
            <FinancingCTAHome />
          </Suspense>
        </LazyOnVisible>

        <LazyOnVisible minHeight="320px" data-gsap-reveal>
          <Suspense fallback={<SectionFallback h="320px" />}>
            <LatestNewsSection />
          </Suspense>
        </LazyOnVisible>


        <LazyOnVisible minHeight="160px" data-gsap-reveal>
          <Suspense fallback={<SectionFallback h="160px" />}>
            <AboutIntroBlock />
          </Suspense>
        </LazyOnVisible>

        {/* RO Auto Market în presă: mențiuni reale din presă, fără reproducerea articolului */}
        {country === 'RO' && (
          <LazyOnVisible minHeight="140px" data-gsap-reveal>
            <Suspense fallback={<SectionFallback h="140px" />}>
              <PressMention />
            </Suspense>
          </LazyOnVisible>
        )}

        {/* BrandsCitiesDirectory eliminat: duplica PopularBrands + PopularCities de mai sus */}

        <LazyOnVisible minHeight="300px" data-gsap-reveal>
          <Suspense fallback={<SectionFallback h="300px" />}>
            <AnswerCapsules />
          </Suspense>
        </LazyOnVisible>

        <div className="px-4 py-8">
          <LazyOnVisible minHeight="200px" data-gsap-reveal>
            <Suspense fallback={<SectionFallback h="200px" />}>
              <FAQSection />
            </Suspense>
          </LazyOnVisible>
        </div>



        {/* Synchronous SEO content: 300+ words + FAQ JSON-LD.
            Placed at the end so it doesn't dilute the UX above, but stays in the DOM for crawlers. */}
        <HomeSEOContent country={country} />

        {/* Autovit-style internal link block: top brands / models / cities +
            per-brand model hubs, generated from public.get_seo_footer_links. */}
        {country === 'RO' && (
          <LazyOnVisible minHeight="240px">
            <Suspense fallback={null}>
              <SEOInternalLinksBlock />
            </Suspense>
          </LazyOnVisible>
        )}

        <section className="container mx-auto px-4 py-6">
          <nav className="flex flex-wrap justify-center gap-4 text-sm" aria-label="Quick links">
            {seo.links.map(link => (
              <SafeLink key={link.to} to={link.to} className="text-primary hover:underline">
                {link.label}
              </SafeLink>

            ))}
          </nav>
        </section>
      </main>
    </div>
  );
};

export default memo(Index);
